import { abmeldenUrl, anmeldenUrl, microsoftAbmeldenUrl } from '../api';
import '../styles/landingpage.css';

/** Kurzbeschreibung der Bereiche auf der Landingpage; `icon` ist ein Pfad im 24er-Raster. */
const BEREICHE: { titel: string; text: string; icon: string }[] = [
  { titel: 'Prozesse', text: 'Prozesslandkarte und Swimlane-Diagramme von grob nach fein – jeder Schritt mit Beschreibung, Quellen und Notizen.',
    icon: 'M4 5h6v4H4zM14 5h6v4h-6zM9 15h6v4H9zM7 9v3h10V9M12 12v3' },
  { titel: 'Entscheidungen', text: 'Offene Fragen mit Varianten, Vor- und Nachteilen und Empfehlung – entschieden wird nachvollziehbar.',
    icon: 'M12 3v4M12 7l-6 5 6 5 6-5zM12 17v4' },
  { titel: 'Aufgaben & Meilensteine', text: 'Liste, Board und Zeitplan für alle Beteiligten – mit Fälligkeiten, Zuständigkeiten und Benachrichtigungen.',
    icon: 'M4 6l2 2 3-3M11 7h9M4 12l2 2 3-3M11 13h9M4 18h5M11 19h9' },
  { titel: 'Tests & Abnahmen', text: 'Testfälle durchführen, Ergebnisse dokumentieren und Abnahmen sauber festhalten.',
    icon: 'M9 12l2 2 4-4M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6z' },
  { titel: 'Cockpit', text: 'Der Projektstand auf einen Blick: Fortschritt, Termine, Risiken und das, was als Nächstes ansteht.',
    icon: 'M4 19h16M7 16v-5M12 16V6M17 16v-8' },
  { titel: 'Dokumente & Protokolle', text: 'Unterlagen, Besprechungsprotokolle und der Versionsverlauf – alles am richtigen Ort im Projekt.',
    icon: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6' },
];

const Linie = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);

/**
 * Öffentliche Startseite von Projects (ohne Anmeldung und nach dem Abmelden): kurze Beschreibung und Anmelden-Knopf.
 * `hinweis` erscheint über der Überschrift (z. B. „Sie sind abgemeldet.“); `ziel` ist die Seite nach der Anmeldung.
 * `keinZugang` (Meldung der API): angemeldet, aber zu keinem Projekt eingeladen – statt „Anmelden“ geht es ans Abmelden.
 */
export function Landingpage({ hinweis, ziel, keinZugang }: { hinweis?: string; ziel?: string; keinZugang?: string }) {
  const href = keinZugang ? abmeldenUrl() : anmeldenUrl(ziel);
  return (
    <div className="landing" data-testid="anmelden">
      <div className="landing-glanz" aria-hidden="true" />
      <header className="landing-kopf">
        <div className="landing-marke brand">
          <img className="logo-iwm-light" src="/iwm_logo.png" alt="IWM" />
          <img className="logo-iwm-dark" src="/iwm_logo_dunkel.png" alt="IWM" />
          <span className="landing-trenner" aria-hidden="true" />
          <span className="landing-name">Projects</span>
        </div>
        <a className="landing-btn klein" href={href}>{keinZugang ? 'Abmelden' : 'Anmelden'}</a>
      </header>

      <main>
        <section className="landing-held">
          <div className="landing-held-text">
            {hinweis && !keinZugang && <div className="landing-hinweis" role="status"><span className="punkt" aria-hidden="true" />{hinweis}</div>}
            {keinZugang && (
              <div className="landing-hinweis abgewiesen" role="alert" data-testid="kein-zugang">
                <span className="punkt" aria-hidden="true" />
                <span><b>Kein Zugang.</b> {keinZugang} Bitte wenden Sie sich an Ihren Ansprechpartner bei der IWM oder melden Sie sich mit einem anderen Konto an.</span>
              </div>
            )}
            <h1>Projekte gemeinsam steuern – <span className="akzent">vom Prozess bis zur Abnahme.</span></h1>
            <p className="landing-lead">
              Projects ist die gemeinsame Arbeitsumgebung für Ihre Einführungsprojekte mit der IWM: Prozesse, Entscheidungen,
              Aufgaben, Meilensteine, Tests und Dokumente an einem Ort – für alle Beteiligten immer auf dem aktuellen Stand.
            </p>
            <div className="landing-aktionen">
              {keinZugang ? (
                <>
                  <a className="landing-btn" href={href} data-testid="kein-zugang-abmelden">Abmelden</a>
                  <span className="landing-zugang">
                    Für ein anderes Konto danach auch{' '}
                    <a href={microsoftAbmeldenUrl} target="_blank" rel="noreferrer">bei Microsoft abmelden</a>.
                  </span>
                </>
              ) : (<>
              <a className="landing-btn" href={href} data-testid="anmelden-knopf">
                <svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Mit Microsoft-Konto anmelden
              </a>
              <span className="landing-zugang">Zugang erhalten Sie per Einladung.</span>
              </>)}
            </div>
          </div>
          <Vorschau />
        </section>

        <section className="landing-bereiche" aria-label="Was Projects bietet">
          {BEREICHE.map((b) => (
            <article key={b.titel} className="landing-karte">
              <div className="landing-icon"><Linie d={b.icon} /></div>
              <h2>{b.titel}</h2>
              <p>{b.text}</p>
            </article>
          ))}
        </section>
      </main>

      <footer className="landing-fuss">
        <span>© {new Date().getFullYear()} IWM</span>
        <span className="landing-konto">
          Mit einem anderen Konto anmelden? Zuerst{' '}
          <a href={microsoftAbmeldenUrl} target="_blank" rel="noreferrer" data-testid="microsoft-abmelden">bei Microsoft abmelden</a>,
          dann hier anmelden.
        </span>
      </footer>
    </div>
  );
}

/** Stilisierte Ansicht der App (Swimlane-Ausschnitt und Aufgaben) – reine Dekoration. */
function Vorschau() {
  return (
    <div className="landing-vorschau" aria-hidden="true">
      <div className="fenster">
        <div className="fenster-leiste"><i /><i /><i /><span>projects.iwm.cloud</span></div>
        <svg className="bahnen" viewBox="0 0 360 190">
          <rect className="bahn a" x="0" y="0" width="360" height="63" />
          <rect className="bahn b" x="0" y="63" width="360" height="64" />
          <rect className="bahn a" x="0" y="127" width="360" height="63" />
          <rect className="bahn-kopf" x="0" y="0" width="26" height="190" />
          <path className="kante" d="M96 31h34M186 31h20v64h22M284 95h20v63h-20M228 158h-34" />
          <rect className="knoten" x="40" y="17" width="56" height="28" rx="6" />
          <rect className="knoten an" x="130" y="17" width="56" height="28" rx="6" />
          <rect className="knoten" x="228" y="81" width="56" height="28" rx="6" />
          <path className="raute" d="M304 144l16 14-16 14-16-14z" transform="translate(0 0)" />
          <rect className="knoten" x="138" y="144" width="56" height="28" rx="6" />
        </svg>
      </div>
      <div className="schwebe karte-aufgabe">
        <span className="haken-deko" />
        <div><b>Stammdaten abstimmen</b><small>fällig Freitag · Vertrieb</small></div>
      </div>
      <div className="schwebe karte-entscheidung">
        <span className="chip-deko">Entschieden</span>
        <b>Variante B übernommen</b>
      </div>
    </div>
  );
}
